/**
 * ScorePanel.tsx — Tract Opportunity Score compact view (Plan 02.1-06)
 *
 * Displays:
 *   - Eyebrow row: tract ID + dominant type + optional ConfidenceBadge
 *   - Headline: integer Opportunity Score + tier word (scoreColor, D-12 thresholds)
 *   - Inline subtitle: SCORE_DEFS.LVI.casualPhrase
 *   - 4 sub-indices (CDPI, CMPI, PPI, Gap) with MetricInfoButton
 *   - "View all metrics ▾" button — calls onOpenFullDetails to open InfoSidePanel
 *
 * Responsive routing (compact card vs Drawer container) is MapShell's concern.
 * ScorePanel is a pure content component — NO positional CSS (no absolute, fixed, or top/right offsets).
 *
 * Props:
 *   tract               — selected census tract
 *   siteScore           — optional site-level score (State B); falls back to tract.lviScore (State A)
 *   siteScoreLoading    — shows '…' placeholder in headline slot while fetching
 *   siteScoreError      — error copy on 404/network failure
 *   confidence          — geocode confidence; shows ConfidenceBadge in eyebrow when set
 *   onOpenFullDetails   — called when user clicks "View all metrics ▾"; MapShell opens InfoSidePanel
 */
import { SCORE_LABELS, dominantTypeLabel } from '../lib/tractModel'
import { getTierWord } from '../lib/scoreTiers'
import { formatSubIndex } from '../lib/siteAnalysis'
import { SCORE_DEFS } from '../lib/scoreDefinitions'
import { GEO_LEVEL_LABELS, GEO_LEVEL_DESCRIPTIONS, type GeoLevel } from '../lib/geoLevel'
import { blockDiffersFromTract, formatClassScore } from '../lib/locationClassification'
import type { LocationClassification } from '../lib/locationClassification'
import { getScoreNarrative } from '../lib/scoreNarrative'
import type { CensusTract, SiteScoreResult } from '../lib/tractModel'
import { ConfidenceBadge } from './ConfidenceBadge'
import { cmpiColor } from './ScoreDrillDown'
import { MetricInfoButton } from './MetricInfoButton'
import { GeoLevelTag } from './GeoLevelTag'
import { Spinner } from './ui/spinner'
import { ChevronRight, Info } from 'lucide-react'
import { ExportPdfButton } from './pdf/ExportPdfButton'
import { RoiCalculatorButton } from './roi/RoiCalculatorButton'

export interface ScorePanelProps {
  /**
   * T-081 F0/F1: nullable. An out-of-coverage result renders this panel with `tract: null`
   * to show the no-coverage feedback independent of a resolved tract — and, crucially, WITHOUT
   * a stale headline score/tract ID beside the "No coverage" banner.
   */
  tract: CensusTract | null
  siteScore?: SiteScoreResult | null
  siteScoreLoading?: boolean
  /** Error message from fetchSiteScore (404 → no-coverage copy, network → unavailable copy). */
  siteScoreError?: string | null
  /** Geocode confidence level from MapSearchBar — shown as a badge in the eyebrow row. */
  confidence?: 'exact' | 'approximate' | null
  /** Opens the InfoSidePanel full-details view. Called when user clicks "View all metrics ▾". */
  onOpenFullDetails?: () => void
  /** The site address for the PDF export header + (T-478) the panel's address line: a searched
   *  (exact) address, or a map-click reverse-geocoded (approximate) one. Null when neither. */
  exportAddress?: string | null
  /** T-478: true when `exportAddress` came from reverse-geocoding a map click (approximate). Shows
   *  the "Approximate address — based on map location" disclaimer in the panel and carries the same
   *  note into the PDF; also seeds the ROI calculator's site context. */
  addressApproximate?: boolean
  /** T-258: state label for the PDF export (app convention: abbreviation, e.g. "GA"). When
   *  present alongside a resolved tract, the "Export PDF" control renders. */
  exportStateLabel?: string | null
  /** T-258: current site pin, drawn on the export's locator map. */
  exportPin?: { lat: number; lng: number } | null
  /**
   * T-628: the site's own block / block-group classification, resolved once by MapShell. The card
   * shows a line from it only when the site's block is a DIFFERENT kind of place than its tract —
   * the case that changes a decision. Everything else stays in the full readout in the panel.
   */
  siteClassification?: LocationClassification | null
}

// D-12: verbatim — do not change thresholds
function scoreColor(value: number): string {
  if (value >= 70) return 'var(--score-high)'
  if (value >= 60) return 'var(--score-good)'
  if (value >= 45) return 'var(--score-moderate)'
  if (value >= 35) return 'var(--score-low)'
  return 'var(--score-poor)'
}

// Site adjustment delta color (D-10: CSS var references only)
// Kept for potential re-use; score breakdown display lives in ScoreDrillDown.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function adjColor(delta: number): string {
  if (delta > 0) return 'var(--adj-positive)'
  if (delta < 0) return 'var(--adj-negative)'
  return 'var(--adj-zero)'
}

// Site adjustment display string (Unicode minus U+2212 for negative)
// Kept for potential re-use; score breakdown display lives in ScoreDrillDown.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function formatAdj(delta: number): string {
  if (delta > 0) return `+${delta.toFixed(1)}`
  if (delta < 0) return `−${Math.abs(delta).toFixed(1)}`
  return '0.0'
}

// Tier words live in lib/scoreTiers (single source of truth, D-12 thresholds). Re-exported here
// for the ranked "Top tracts in view" list (T-222) and any existing importers of the panel's
// language, so those call sites are unaffected by the extraction.
export { getTierWord }

export function ScorePanel({
  tract,
  siteScore,
  siteScoreLoading = false,
  siteScoreError,
  confidence,
  onOpenFullDetails,
  exportAddress = null,
  addressApproximate = false,
  exportStateLabel = null,
  exportPin = null,
  siteClassification = null,
}: ScorePanelProps) {
  // T-081 F1: no tract → derive no score from it (avoids a null deref). The score row is
  // shown only while loading or when there IS a score to show — so an out-of-coverage result
  // never renders a (stale) number beside the "No coverage" banner.
  const displayScore = siteScore ? siteScore.opportunityScore : tract ? tract.lviScore : null
  const showScoreRow = siteScoreLoading || displayScore !== null

  // ── T-547: the card's single geographic-level line ────────────────────────────────────────────
  // Noreen: "If everything on that card is the same level, a single line noting the level is
  // enough. No need for per-metric tags there." Her rule is conditional, so the line is DERIVED
  // from what is actually on screen — it appears only when the levels genuinely agree, and the card
  // stays silent when they do not. A hardcoded "tract-level" would be a lie in State B.
  // Sub-scores: CDPI, CMPI, Gap use site values when available; PPI always tract
  const subIndexRows = tract
    ? ([
        {
          label: SCORE_LABELS.cdpi,
          value: siteScore?.v2?.siteCdpi ?? tract.cdpiScore,
          key: 'CDPI',
          level: (siteScore?.v2 ? 'site' : 'tract') as GeoLevel,
        },
        {
          label: SCORE_LABELS.cmpi,
          value: siteScore?.v2?.siteCmpi ?? tract.cmpiScore,
          key: 'CMPI',
          level: (siteScore?.v2 ? 'site' : 'tract') as GeoLevel,
        },
        {
          label: SCORE_LABELS.gap_score,
          value: siteScore?.v2?.gapScore ?? tract.gapScore,
          key: 'Gap',
          level: (siteScore?.v2 ? 'site' : 'tract') as GeoLevel,
        },
        {
          label: SCORE_LABELS.ppi_v11,
          value: tract.ppiScore,
          key: 'PPI',
          level: 'tract' as GeoLevel,
        },
      ] as const)
    : []
  const showSubIndices = !siteScoreError && tract !== null

  // The headline's level follows the NUMBER, not the definition: `SCORE_DEFS.LVI.level` is 'site',
  // but in State A and in the degraded state the figure on screen is the tract LVI. Reading the
  // definition alone would mislabel both.
  const headlineLevel: GeoLevel =
    siteScore && !siteScore.v2?.degraded ? SCORE_DEFS.LVI.level : 'tract'

  // Built from the SAME row list that renders below, so a metric added to the card cannot slip past
  // the uniformity check and let the line claim a level the new row does not have.
  const levelsShown: GeoLevel[] = [
    ...(showScoreRow && displayScore !== null ? [headlineLevel] : []),
    ...(showSubIndices
      ? subIndexRows.map((r) =>
          r.key === 'PPI'
            ? ('tract' as GeoLevel)
            : siteScore?.v2
              ? ('site' as GeoLevel)
              : SCORE_DEFS[r.key].level,
        )
      : []),
  ]
  // ── T-628: the site's own block, on the card, when it contradicts the tract ──────────────────
  // Ricardo's rule: block data lifts onto the compact card when it is available AND crucial. A
  // block whose class matches its tract adds nothing to a headline that already says so; a block
  // whose class DIFFERS is the whole point — a corridor site reading as residential is exactly the
  // confusion the audit found. Absent block coverage stays silent, per the same ruling.
  const blockLine =
    tract && blockDiffersFromTract(siteClassification, tract.dominantType)
      ? siteClassification!.block!
      : null

  const uniformLevel =
    levelsShown.length > 0 && levelsShown.every((l) => l === levelsShown[0]) ? levelsShown[0] : null

  // The degraded state ALREADY carries a level statement ("Tract-level score · site LVI pending
  // data"), so adding this one would put two on the same small card — which is neither the "single
  // line" Noreen asked for nor consistent with T-352, which suppressed a redundant line in exactly
  // this state for exactly this reason. One level statement per state: this line, or that note.
  const degradedNoteShown = Boolean(siteScore && siteScore.v2?.degraded)

  return (
    <div className="bg-card rounded-xl shadow-md px-4 py-3 font-sans">
      {/* Confidence badge only — tract ID and classification removed per Noreen's request */}
      {tract && confidence && (
        <div className="flex items-center justify-end mb-1">
          <ConfidenceBadge confidence={confidence} />
        </div>
      )}

      {/* T-478: a map-clicked site has no search-bar text, so surface its reverse-geocoded address
          here — with a clear disclaimer that it is approximate (derived from the clicked point, not
          a typed street address). Only for the approximate (map-derived) case; a searched address is
          already shown in the search bar. */}
      {tract && exportAddress && addressApproximate && (
        <p data-testid="approximate-address" className="mb-1 text-xs text-secondary-foreground">
          {exportAddress}
          <span className="mt-0.5 block text-[0.7rem] italic text-muted-foreground">
            Approximate address — based on map location
          </span>
        </p>
      )}

      {/* Headline row: "Opportunity Score" label left; integer score + tier word right.
          Hidden entirely in the no-coverage state (no tract, no score) so nothing misleading
          shows beside the banner below. */}
      {showScoreRow && (
        <div className="flex items-center justify-between py-2 border-b border-border">
          <span className="text-sm font-bold text-foreground">{SCORE_LABELS.lvi}</span>
          {siteScoreLoading || displayScore === null ? (
            <Spinner size={22} className="text-muted-foreground" label="Scoring location" />
          ) : (
            <div className="flex flex-col items-end">
              <div className="flex items-baseline gap-2">
                <span
                  style={{ fontSize: '2rem', fontWeight: 700, color: scoreColor(displayScore) }}
                >
                  {Math.round(displayScore)}
                </span>
                <span className="text-xs" style={{ color: scoreColor(displayScore) }}>
                  {getTierWord(displayScore)}
                </span>
              </div>
              {/* T-159 (BEAD-0067): with a site-level score the headline is the COMPOSITE
                  Opportunity Score, which can diverge from the tract's own LVI. Surface the raw
                  tract LVI baseline directly beneath it so a user who knows the legacy per-tract
                  number recognises it and doesn't read the composite as a "wrong" LVI. Only in
                  State B — in State A the headline already IS the tract LVI, so this would repeat it.

                  T-352: when the site score is DEGRADED (tract `sam` unavailable → the API could
                  not recompute a site-specific LVI and returned the tract LVI), the headline number
                  IS the tract LVI. So the "Tract Score" line would just repeat it — replace it with
                  a truth-in-labeling note that the number is tract-level and the site-specific LVI
                  is pending data, rather than implying a live site score that happens to match. */}
              {siteScore && siteScore.v2?.degraded && (
                <span className="text-[0.7rem] leading-tight text-muted-foreground flex items-center gap-1">
                  <Info size={11} aria-hidden="true" className="flex-shrink-0" />
                  <span>Tract-level score · site LVI pending data</span>
                </span>
              )}
              {siteScore && !siteScore.v2?.degraded && (
                <span className="text-[0.7rem] leading-tight text-muted-foreground">
                  Tract Score:{' '}
                  <span className="font-semibold text-foreground">
                    {siteScore.lviBaseline.toFixed(1)}
                  </span>
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Inline subtitle. With a resolved tract + score, this is the deterministic "why this grade"
          narrative (T-162), banded on the DISPLAYED score so it always matches the tier word above.
          While a location is still scoring (no displayScore yet) it falls back to the static LVI
          casualPhrase. `text-muted-foreground` is a theme token → legible in light and dark (T-156). */}
      {showScoreRow && (
        <p className="text-xs italic text-muted-foreground mt-1 mb-2">
          {tract && !siteScoreLoading && displayScore !== null
            ? getScoreNarrative(tract, displayScore)
            : SCORE_DEFS.LVI.casualPhrase}
        </p>
      )}

      {/* Error state */}
      {siteScoreError && (
        <p className="px-2 py-1.5 bg-destructive/10 text-destructive rounded-md text-xs mb-2">
          {siteScoreError}
        </p>
      )}

      {/* Sub-indices (4 rows) — each with a MetricInfoButton. Requires a resolved tract. */}
      {!siteScoreError && tract && (
        <div className="space-y-0">
          {subIndexRows.map(({ label, value, key, level }) => (
            <div
              key={key}
              className="flex items-center justify-between py-1.5 border-t border-border/40"
            >
              <div className="flex items-center gap-0.5 text-sm text-secondary-foreground">
                {label}
                <MetricInfoButton metricKey={key} label={label} />
                <GeoLevelTag level={level} metricLabel={label} />
              </div>
              {/* T-199: CMPI is the one inverse index (higher = more crowded market = worse for a
                  new entrant), so band it on the inverted scale — cmpiColor — while every other
                  sub-index stays higher-is-better. The displayed number is unchanged either way. */}
              <span
                className="text-sm font-bold"
                style={{ color: key === 'CMPI' ? cmpiColor(value) : scoreColor(value) }}
              >
                {formatSubIndex(value)}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* T-547: one line for the whole card instead of a tag per metric — rendered only where every
          figure above really is the same geography (see `uniformLevel`). Muted and provenance-sized,
          matching GeoLevelTag: it describes the numbers, it is not one of them. */}
      {/* T-628: the site's own block, when it disagrees with the tract around it. */}
      {blockLine && (
        <p
          data-testid="card-block-line"
          className="mt-1.5 text-[0.7rem] leading-tight text-muted-foreground"
        >
          This block is <span className="font-semibold text-foreground">{blockLine.label}</span>
          {blockLine.score !== null && (
            <>
              {' '}
              · {blockLine.scoreLabel}{' '}
              <span className="font-semibold text-foreground">
                {formatClassScore(blockLine.score, blockLine.scoreScale)}
              </span>
            </>
          )}
        </p>
      )}

      {/* T-628: the level line is a claim about EVERY figure on the card, so it cannot stand once
          a block-level figure is on the card too. It yields to the block line rather than being
          reworded — the block line already names its own geography. */}
      {showSubIndices && uniformLevel && !degradedNoteShown && !blockLine && (
        <p
          data-testid="card-level-note"
          title={GEO_LEVEL_DESCRIPTIONS[uniformLevel]}
          className="mt-1.5 text-[0.7rem] leading-tight text-muted-foreground"
        >
          All figures on this card are {GEO_LEVEL_LABELS[uniformLevel].toLowerCase()}-level.
        </p>
      )}

      {/* "View all metrics" — opens InfoSidePanel (desktop) or Drawer full-height (mobile).
          Requires a resolved tract to drill into. */}
      {!siteScoreError && tract && (
        <button
          onClick={onOpenFullDetails}
          className="flex w-full items-center justify-between
                     min-h-[44px] mt-2 border-t border-border/40 pt-2 px-0.5
                     text-xs text-muted-foreground hover:text-primary
                     cursor-pointer bg-transparent outline-none
                     focus-visible:ring-2 focus-visible:ring-ring rounded-sm
                     transition-colors"
        >
          <span>View all metrics</span>
          <ChevronRight size={14} aria-hidden="true" />
        </button>
      )}

      {/* T-258: Export the current tract as a branded one-pager PDF. Renders only with a
          resolved tract AND a state label (which MapShell supplies); the export needs both. */}
      {!siteScoreError && tract && exportStateLabel && (
        <ExportPdfButton
          tract={tract}
          address={exportAddress}
          addressApproximate={addressApproximate}
          stateLabel={exportStateLabel}
          pin={exportPin}
          siteScore={siteScore}
        />
      )}

      {/* T-020 Phase 2 / T-627: the ROI calculator, launched contextually from this selected site's
          panel (placement B). It was admin-only while it was an internal surface; it now renders
          for every signed-in user, so this row action is part of the standard panel. */}
      {!siteScoreError && tract && (
        <RoiCalculatorButton siteAddress={exportAddress} addressApproximate={addressApproximate} />
      )}
    </div>
  )
}
